
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { Memory } from "@/types/memory";

export const useMemoryOperations = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMemories, setSelectedMemories] = useState<number[]>([]);

  const fetchMemories = useCallback(async (userId: string, selectedPersona: string | null) => {
    setIsLoading(true);
    try {
      console.log('Fetching memories for userId:', userId, 'persona:', selectedPersona);
      
      // Build the query for userData table
      let query = supabase
        .from('userData')
        .select('*')
        .eq('userId', userId)
        .order('created_at', { ascending: false });

      // Filter by category if a specific persona is selected
      if (selectedPersona) {
        query = query.eq('category', selectedPersona);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching memories:', error);
        // If the table doesn't exist, show a helpful message
        if (error.code === '42P01') {
          toast({
            variant: "destructive",
            title: "User data table not found",
            description: "The user data table hasn't been created yet. Memories will appear here once you start creating them."
          });
          setMemories([]);
          return;
        }
        throw error;
      }

      console.log('Fetched memories:', data);
      
      // Transform the data and decrypt encrypted memories
      const transformedData = data?.map(item => ({
        ...item,
        firstName: '', // Set default value as this field doesn't exist in userData table
        security_level: (item.security_level as 'low' | 'medium' | 'high') || 'low'
      })) || [];

      // Decrypt memories that have encrypted content
      const decryptedMemories = await Promise.all(
        transformedData.map(async (memory) => {
          if ((memory as any).memory_encrypted) {
            try {
              const { data: decryptResult, error: decryptError } = await supabase.functions.invoke('decryptMemory', {
                body: { memory_id: memory.id }
              });

              if (decryptError) {
                console.error('Error decrypting memory:', decryptError);
                // Keep the original memory if decryption fails
                return memory;
              }

              // Replace the memory content with decrypted content
              return {
                ...memory,
                memory: decryptResult.decrypted_memory || memory.memory
              };
            } catch (error) {
              console.error('Error calling decrypt function:', error);
              // Keep the original memory if decryption fails
              return memory;
            }
          }
          // Return memory as-is if not encrypted
          return memory;
        })
      );

      setMemories(decryptedMemories);
    } catch (error) {
      console.error('Error fetching memories:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch memories"
      });
      setMemories([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const deleteMemory = useCallback(async (memoryId: number) => {
    try {
      console.log('Attempting to delete memory with ID:', memoryId);
      console.log('Memory ID type:', typeof memoryId);
      
      // First, let's check if the memory exists
      const { data: existingMemory, error: fetchError } = await supabase
        .from('userData')
        .select('id')
        .eq('id', memoryId)
        .single();
      
      console.log('Existing memory check:', { existingMemory, fetchError });
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking if memory exists:', fetchError);
        throw fetchError;
      }
      
      if (!existingMemory) {
        console.log('Memory not found in database');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Memory not found in database"
        });
        return;
      }
      
      // Now attempt the delete
      const { data, error, count } = await supabase
        .from('userData')
        .delete({ count: 'exact' })
        .eq('id', memoryId);

      console.log('Delete response:', { data, error, count });

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      if (count === 0) {
        console.log('No rows were deleted');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete memory - no rows affected"
        });
        return;
      }

      console.log('Memory deleted successfully, updating local state');
      setMemories(prev => prev.filter(memory => memory.id !== memoryId));
      // Also remove from selected memories if it was selected
      setSelectedMemories(prev => prev.filter(id => id !== memoryId));
      toast({
        title: "Success",
        description: "Memory deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting memory:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete memory"
      });
    }
  }, [toast]);

  const bulkDeleteMemories = useCallback(
    async (memoryIds: number[]) => {
      if (!currentUser) {
        console.error("No authenticated user found");
        return false;
      }

      setIsDeleting(true);
      
      try {
        console.log("Bulk deleting memories:", memoryIds);
        
        const { error } = await supabase
          .from("userData")
          .delete()
          .in("id", memoryIds)
          .eq("userId", currentUser.id);

        if (error) {
          console.error("Error bulk deleting memories:", error);
          throw error;
        }

        toast({
          title: "Success",
          description: `Successfully deleted ${memoryIds.length} memories`,
        });

        return true;
      } catch (error) {
        console.error("Error in bulk delete:", error);
        toast({
          title: "Error",
          description: "Failed to delete memories. Please try again.",
          variant: "destructive",
        });
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [currentUser, toast]
  );

  const recategorizeMemory = useCallback(
    async (memoryId: number, newCategory: string) => {
      if (!currentUser) {
        console.error("No authenticated user found");
        return false;
      }

      setIsRecategorizing(true);
      
      try {
        console.log("Recategorizing memory:", memoryId, "to category:", newCategory);
        
        const { error } = await supabase
          .from("userData")
          .update({ 
            category: newCategory,
          })
          .eq("id", memoryId)
          .eq("userId", currentUser.id);

        if (error) {
          console.error("Error recategorizing memory:", error);
          throw error;
        }

        toast({
          title: "Success",
          description: "Memory recategorized successfully",
        });

        return true;
      } catch (error) {
        console.error("Error in recategorize:", error);
        toast({
          title: "Error",
          description: "Failed to recategorize memory. Please try again.",
          variant: "destructive",
        });
        return false;
      } finally {
        setIsRecategorizing(false);
      }
    },
    [currentUser, toast]
  );

  const updateSecurityLevel = useCallback(
    async (memoryId: number, securityLevel: 'low' | 'medium' | 'high') => {
      if (!currentUser) {
        console.error("No authenticated user found");
        return false;
      }

      try {
        console.log("Updating security level for memory:", memoryId, "to:", securityLevel);
        
        // Update in the database - using any to bypass TypeScript type checking
        const { error } = await supabase
          .from("userData")
          .update({ 
            security_level: securityLevel,
          } as any)
          .eq("id", memoryId)
          .eq("userId", currentUser.id);

        if (error) {
          console.error("Error updating security level:", error);
          throw error;
        }

        // Update local state
        setMemories(prev => prev.map(memory => 
          memory.id === memoryId 
            ? { ...memory, security_level: securityLevel }
            : memory
        ));

        toast({
          title: "Success",
          description: "Security level updated successfully",
        });

        return true;
      } catch (error) {
        console.error("Error updating security level:", error);
        toast({
          title: "Error",
          description: "Failed to update security level. Please try again.",
          variant: "destructive",
        });
        return false;
      }
    },
    [currentUser, toast]
  );

  return {
    memories,
    isLoading,
    selectedMemories,
    setSelectedMemories,
    fetchMemories,
    deleteMemory,
    bulkDeleteMemories,
    recategorizeMemory,
    updateSecurityLevel,
    isDeleting,
    isRecategorizing,
  };
};
